import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { companyOperatorsApi, teamMembersApi } from "../../../api";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { MobileSelect } from "../../../components/ui/MobileSelect";
import { QueryErrorBanner } from "../../../components/feedback/QueryFeedback";
import { useAuth } from "../../../context/AuthContext";
import { queryKeys } from "../../../queryKeys";
import { isOwner } from "../../../utils/roles";
import type { CompanyOperatorAccount, TeamMember, TeamRole } from "../../../types";

const ROLES: TeamRole[] = ["operator", "supervisor", "admin"];
const ROLE_OPTIONS = ROLES.map((r) => ({ value: r, label: r }));
const EMAIL_REGEX = /^\S+@\S+\.\S+$/;

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

type Props = {
  token: string;
  compact?: boolean;
};

/** Company-wide gate team: the same operators are available on every event. */
export const LiveTeamMembersPanel: React.FC<Props> = ({ token, compact = false }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("operator");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState<TeamRole>("operator");
  const [pendingRemove, setPendingRemove] = useState<TeamMember | null>(null);
  const [managingMember, setManagingMember] = useState<TeamMember | null>(null);
  const [manageEmail, setManageEmail] = useState("");
  const [managePassword, setManagePassword] = useState("");

  const operatorAccountsQuery = useQuery({
    queryKey: queryKeys.companyOperatorAccounts(),
    queryFn: () => companyOperatorsApi.list(token),
    enabled: Boolean(token) && isOwner(user),
  });

  const manageMutation = useMutation({
    mutationFn: async () => {
      const trimmedEmail = manageEmail.trim();
      if (!EMAIL_REGEX.test(trimmedEmail)) throw new Error("Enter a valid email address.");
      const existing = (operatorAccountsQuery.data ?? []).find(
        (acct: CompanyOperatorAccount) => acct.email.toLowerCase() === trimmedEmail.toLowerCase(),
      );
      if (existing) {
        if (!managePassword.trim()) throw new Error("Enter a new password, or leave email unchanged.");
        return companyOperatorsApi.resetCredentials(
          existing.id,
          { email: trimmedEmail, password: managePassword.trim() || undefined },
          token,
        );
      }
      if (!managePassword.trim()) throw new Error("Set a password for this operator's login.");
      return companyOperatorsApi.create(
        { email: trimmedEmail, full_name: managingMember?.name || trimmedEmail, password: managePassword.trim() },
        token,
      );
    },
    onSuccess: async () => {
      toast.success("Operator login saved");
      setManagingMember(null);
      setManageEmail("");
      setManagePassword("");
      await queryClient.invalidateQueries({ queryKey: queryKeys.companyOperatorAccounts() });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not save operator login.");
    },
  });

  const membersQuery = useQuery({
    queryKey: queryKeys.teamMembers(),
    queryFn: () => teamMembersApi.list(token),
    enabled: Boolean(token),
    refetchInterval: 15000,
  });

  const invalidateMembers = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.teamMembers() }),
      queryClient.invalidateQueries({ queryKey: ["eventOs"] }),
      queryClient.invalidateQueries({ queryKey: ["events"] }),
    ]);
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const trimmedEmail = email.trim();
      if (!trimmedName) throw new Error("Name is required.");
      if (!EMAIL_REGEX.test(trimmedEmail)) throw new Error("Enter a valid email address.");
      return teamMembersApi.create({ name: trimmedName, email: trimmedEmail, role }, token);
    },
    onSuccess: async () => {
      toast.success("Team member added");
      setName("");
      setEmail("");
      setRole("operator");
      await invalidateMembers();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not add team member.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const trimmedName = editName.trim();
      const trimmedEmail = editEmail.trim();
      if (!trimmedName) throw new Error("Name is required.");
      if (!EMAIL_REGEX.test(trimmedEmail)) throw new Error("Enter a valid email address.");
      return teamMembersApi.update(memberId, { name: trimmedName, email: trimmedEmail, role: editRole }, token);
    },
    onSuccess: () => {
      toast.success("Team member updated");
      setEditingId(null);
      void invalidateMembers();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not update team member.");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => teamMembersApi.remove(memberId, token),
    onSuccess: () => {
      toast.success("Team member removed");
      void invalidateMembers();
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Could not remove team member.");
    },
  });

  const members = useMemo<TeamMember[]>(() => membersQuery.data ?? [], [membersQuery.data]);

  const startEdit = (member: TeamMember) => {
    setEditingId(member.id);
    setEditName(member.name);
    setEditEmail(member.email);
    setEditRole((member.role as TeamRole) || "operator");
  };

  useEffect(() => {
    if (editingId && !members.some((m) => m.id === editingId)) {
      setEditingId(null);
    }
  }, [editingId, members]);

  const submitDisabled = createMutation.isPending || !name.trim() || !EMAIL_REGEX.test(email.trim());

  return (
    <>
      <div className={`eventos-card${compact ? " eventos-card--team-compact" : ""}`}>
        <div className="eventos-label">Team on gate</div>
        {compact ? null : (
          <div className="eventos-subtle">
            Your team is shared across all events. Add anyone who will be operating the gate: type their name, email
            and role &mdash; no signup required.
          </div>
        )}

        <form
          className="eventos-form"
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
        >
          <div className={`eventos-form-grid${compact ? " eventos-form-grid--team-compact" : ""}`}>
            <label className="eventos-field">
              <span className="eventos-field-label">Name</span>
              <input
                className="eventos-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                autoComplete="off"
              />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">Email</span>
              <input
                className="eventos-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                autoComplete="off"
              />
            </label>
            <label className="eventos-field">
              <span className="eventos-field-label">Role</span>
              <MobileSelect
                label="Role"
                className="eventos-select"
                value={role}
                onChange={(v) => setRole(v as TeamRole)}
                options={ROLE_OPTIONS}
              />
            </label>
            {compact ? (
              <div className="eventos-field eventos-field--team-add">
                <span className="eventos-field-label" aria-hidden="true">
                  &nbsp;
                </span>
                <button type="submit" className="eventos-primary-btn" disabled={submitDisabled}>
                  {createMutation.isPending ? "Adding…" : "Add"}
                </button>
              </div>
            ) : null}
          </div>
          {compact ? null : (
            <button type="submit" className="eventos-primary-btn" disabled={submitDisabled}>
              {createMutation.isPending ? "Adding…" : "Add team member"}
            </button>
          )}
        </form>

        {membersQuery.isError ? (
          <QueryErrorBanner
            message={membersQuery.error instanceof Error ? membersQuery.error.message : "Could not load team."}
            onRetry={() => void membersQuery.refetch()}
            retrying={membersQuery.isFetching}
          />
        ) : null}

        {membersQuery.isPending ? (
          <div className="eventos-empty">Loading team…</div>
        ) : members.length === 0 ? (
          <div className="eventos-empty">
            {compact ? "No team members yet." : "No team members yet. Add operators who will scan tickets at the door."}
          </div>
        ) : (
          <div className="eventos-table-wrap">
            <table className="eventos-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  {!compact ? <th>Added</th> : null}
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.id}>
                    {editingId === member.id ? (
                      <>
                        <td>
                          <input
                            className="eventos-input"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Full name"
                          />
                        </td>
                        <td>
                          <input
                            className="eventos-input"
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            placeholder="name@company.com"
                          />
                        </td>
                        <td colSpan={compact ? 1 : 2}>
                          <MobileSelect
                            label="Role"
                            className="eventos-select"
                            value={editRole}
                            onChange={(v) => setEditRole(v as TeamRole)}
                            options={ROLE_OPTIONS}
                          />
                        </td>
                        <td>
                          <div className="app-shell-table-actions">
                            <button
                              type="button"
                              className="eventos-primary-btn"
                              disabled={updateMutation.isPending}
                              onClick={() => updateMutation.mutate(member.id)}
                            >
                              Save
                            </button>
                            <button type="button" className="eventos-nav-btn" onClick={() => setEditingId(null)}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="team-cell team-cell--name">{member.name}</td>
                        <td className="team-cell team-cell--email">{member.email}</td>
                        <td className="team-cell team-cell--role">{member.role}</td>
                        {compact ? null : (
                          <td className="team-cell team-cell--added">{formatWhen(member.created_at)}</td>
                        )}
                        <td className="team-cell team-cell--actions">
                          <div className="app-shell-table-actions">
                            <button type="button" className="eventos-nav-btn" onClick={() => startEdit(member)}>
                              Edit
                            </button>
                            {isOwner(user) ? (
                              <button
                                type="button"
                                className="eventos-nav-btn"
                                onClick={() => {
                                  setManagingMember(member);
                                  setManageEmail(member.email || "");
                                  setManagePassword("");
                                }}
                              >
                                Manage
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="eventos-nav-btn"
                              disabled={removeMutation.isPending}
                              onClick={() => setPendingRemove(member)}
                            >
                              Remove
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingRemove)}
        title="Remove team member?"
        message={pendingRemove ? `${pendingRemove.name} will be removed from your team on every event.` : ""}
        confirmLabel="Remove"
        danger
        loading={removeMutation.isPending}
        onConfirm={() => {
          if (!pendingRemove) return;
          removeMutation.mutate(pendingRemove.id, {
            onSettled: () => setPendingRemove(null),
          });
        }}
        onCancel={() => setPendingRemove(null)}
      />

      {managingMember ? (
        <div
          className="confirm-dialog__backdrop"
          role="presentation"
          onClick={() => setManagingMember(null)}
        >
          <div
            className="confirm-dialog"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="confirm-dialog__title">Manage login for {managingMember.name}</h2>
            <div className="confirm-dialog__message eventos-subtle">
              Set the email and password this operator uses to sign in. Leave password blank only if just changing
              the email on an existing login.
            </div>
            <form
              className="eventos-form"
              onSubmit={(e) => {
                e.preventDefault();
                manageMutation.mutate();
              }}
            >
              <label className="eventos-field">
                <span className="eventos-field-label">Login email</span>
                <input
                  className="eventos-input"
                  type="email"
                  value={manageEmail}
                  onChange={(e) => setManageEmail(e.target.value)}
                  placeholder="name@company.com"
                  autoComplete="off"
                />
              </label>
              <label className="eventos-field">
                <span className="eventos-field-label">Password</span>
                <input
                  className="eventos-input"
                  type="password"
                  value={managePassword}
                  onChange={(e) => setManagePassword(e.target.value)}
                  placeholder="New password"
                  autoComplete="new-password"
                />
              </label>
              <div className="app-shell-table-actions">
                <button type="submit" className="eventos-primary-btn" disabled={manageMutation.isPending}>
                  {manageMutation.isPending ? "Saving…" : "Save login"}
                </button>
                <button
                  type="button"
                  className="eventos-nav-btn"
                  onClick={() => {
                    setManagingMember(null);
                    setManageEmail("");
                    setManagePassword("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
};
